import { band, fmtScore, MAX_SCORE } from "@/lib/heat";
import { topicsByArea } from "@/lib/topics";

const barColor = (pct) => band(pct * MAX_SCORE).hex;

const Row = ({ t, area }) => (
  <div className="avoid-break py-2" data-testid={`topic-row-${area}-${t.key}`}>
    <div className="grid grid-cols-[1fr_10rem] items-center gap-4">
      <span className="font-display text-sm font-semibold text-slate-900">{t.label}</span>
      <div className="h-2.5 w-full rounded-full bg-slate-100">
        <div className="h-2.5 rounded-full" style={{ width: `${Math.max(4, Math.round(t.pct * 100))}%`, background: barColor(t.pct) }} />
      </div>
    </div>
    <div className="text-xs text-slate-500" data-testid={`topic-answer-${area}-${t.key}`}>{t.answerFull}</div>
  </div>
);

const AreaPage = ({ a, score, descriptions }) => {
  const b = band(score);
  const focus = a.rows.slice(0, 2);
  return (
    <section className="page-break mt-10 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 print-plain" data-testid={`topic-area-${a.key}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-3 border-b-2 border-[#1F4266] pb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Your answers by topic</div>
          <h2 className="text-2xl font-extrabold tracking-tight text-[#1F4266]">{a.label}</h2>
        </div>
        <div className="text-sm">
          <span className="font-semibold" style={{ color: b.hex }}>{b.label}</span>
          <span className="ml-2 font-mono text-slate-600">{fmtScore(score)} / 36</span>
        </div>
      </div>
      <p className="mt-2 text-xs text-slate-500">Lowest rating to highest. Some topics belong to two areas, so they appear on both pages.</p>
      <div className="mt-2 divide-y divide-slate-100">
        {a.rows.map((t) => <Row key={t.key} t={t} area={a.key} />)}
      </div>
      {focus.length > 0 && (
        <div className="mt-5 space-y-3" data-testid={`topic-focus-${a.key}`}>
          <h3 className="font-display text-base font-bold text-slate-900">Your two lowest-rated {a.label} topics</h3>
          {focus.map((t) => (
            <div key={t.key} className="avoid-break rounded-xl bg-slate-50 p-4" data-testid={`topic-focus-${a.key}-${t.key}`}>
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-display font-semibold text-[#1F4266]">{t.label}</span>
                <span className="text-xs text-slate-500">You rated: {t.answerFull}</span>
              </div>
              {descriptions?.[t.key] && <p className="mt-1.5 text-sm leading-relaxed text-slate-700">{descriptions[t.key]}</p>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export const TopicAnswers = ({ topics, scores, descriptions }) => {
  if (!topics?.length) return null;
  return (
    <div data-testid="topic-answers">
      {topicsByArea(topics).map((a) => (
        <AreaPage key={a.key} a={a} score={scores?.[a.key] ?? 0} descriptions={descriptions} />
      ))}
    </div>
  );
};
